<template>
  <div>
    <page-header
      :actions="headerActionsConfig"
      left-side-class="col-12 col-md-7"
      :page="headerConfig"
      right-side-class="col-12 col-md-5"
    />
    <cost-management-services
      v-if="isAddEditMode"
      :durations="filterOptions.durations"
      :location-types="filterOptions.locationTypes"
      :modality-types="filterOptions.modalities"
      :services="filterOptions.services"
      @on-cancel="onCancelHandler"
      @on-save="onSaveCostHandler"
    />
    <filter-manager
      ref="filterComponent"
      v-model="filterModel"
      class="mb-4"
      :filters="filtersConfig"
      @search="onSearchHandler"
      @service="onChangeServiceHandler"
    />
    <custom-separator class="my-4" text="Resultado de Servicios y Costos" />
    <custom-result-not-found v-if="results.length == 0" />
    <template v-else>
      <result-row
        v-for="(row, idx) of results"
        :key="`result-${idx}`"
        class="mb-3"
        :data="row"
        @on-cancel="setEditMode(idx, false)"
        @on-delete="onDeleteCostHandler(row)"
        @on-edit="setEditMode(idx, true)"
        @on-save="onSaveEditCostHandler"
      />
      <pagination
        v-if="pagination.totalItemCount > pagination.pageSize"
        :data="pagination"
        show-text
        @pagination-go-page="goToPageHandler"
      />
    </template>
  </div>
</template>

<script>
import PageHeader from 'PageHeader';
import FilterManager from 'FilterManager';
import CustomSeparator from 'custom-separator';
import CustomResultNotFound from 'custom-result-not-found';
import Pagination from 'pagination';
import { CostManagementServices, ResultRow } from './components';
import {
  getServiceCostFilters as _getServiceCostFilters,
  getDisciplinesRP as _getDisciplinesRP,
  searchServiceCost as _searchServiceCost,
  saveCost as _saveCost,
  editCost as _editCost,
  deleteCost as _deleteCost,
} from '@/services/uspqrp/service-cost.api.js';
import alertMixin from '@quisit/mipe-utils/mixins/alert.mixin.js';

const STORE_MODULE_NAME = 'CostManagementServicesModule';
const DEFAULT_PAGE_SIZE = 20;

export default {
  name: 'CostoManagementServices',
  mixins: [alertMixin],
  components: {
    PageHeader,
    FilterManager,
    CustomSeparator,
    CustomResultNotFound,
    Pagination,
    CostManagementServices,
    ResultRow,
  },
  data: () => ({
    moduleName: STORE_MODULE_NAME,
    filterOptions: {
      locationTypes: [],
      services: [],
      disciplines: [],
      durations: [],
      modalities: [],
    },
    filterModel: {},
    isAddEditMode: false,
    pagination: {
      currentPage: 1,
      totalItemCount: 1,
      pageCount: 1,
      count: 1,
      pageSize: DEFAULT_PAGE_SIZE,
    },
    results: [],
  }),
  computed: {
    headerConfig() {
      return {
        title: 'Configuración de Servicios y Costos',
        icon: 'fas fa-clipboard-list',
        routes: ['Inicio', 'USPQRP', 'Configuración de Servicios y Costos'],
      };
    },
    headerActionsConfig() {
      return [
        {
          text: 'Añadir',
          icon: 'fas fa-file-plus pr-2',
          variant: 'success ml-0',
          visible: !this.isAddEditMode,
          click: () => this.onAddHandler(),
        },
      ];
    },
    filtersConfig() {
      return [
        {
          name: 'locationType',
          component: 'CustomDropDown',
          options: this.filterOptions.locationTypes,
          placeholder: 'Seleccione Tipo de Lugar',
          fieldName: 'name',
          fieldKey: 'id',
          clearable: true,
          class: 'col-12 col-md-4',
        },
        {
          name: 'service',
          component: 'CustomDropDown',
          options: this.filterOptions.services,
          placeholder: 'Seleccione Servicio',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
        },
        {
          name: 'discipline',
          component: 'CustomDropDown',
          options: this.filterOptions.disciplines,
          placeholder: 'Seleccione Disciplina',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
        },
        {
          name: 'duration',
          component: 'CustomDropDown',
          options: this.filterOptions.durations,
          label: 'Duración:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'modality',
          component: 'CustomDropDown',
          options: this.filterOptions.modalities,
          label: 'Modalidad:',
          placeholder: 'Seleccione',
          fieldName: 'name',
          fieldKey: 'id',
          class: 'col-12 col-md-4',
          clearable: true,
          isAdvanced: true,
        },
        {
          name: 'cost',
          component: 'CustomInput',
          label: 'Costo Máximo:',
          placeholder: 'Escriba Costo',
          class: 'col-12 col-md-4',
          isAdvanced: true,
        },
      ];
    },
  },
  async mounted() {
    await Promise.all([this.getInitFilters(), this.searchServiceCost()]);
  },
  methods: {
    async getInitFilters() {
      const res = (await _getServiceCostFilters()).data;
      if (Object.values(res).length) {
        this.filterOptions = {
          locationTypes: res.locationTypes,
          services: res.serviceTypes,
          durations: res.therapyDurations,
          modalities: res.serviceModalityTypes,
        };
      }
    },
    async onChangeServiceHandler(value) {
      this.filterModel.discipline = null;
      if (value?.id) {
        const disciplines = (await _getDisciplinesRP(value.id)).data;
        this.filterOptions = {
          ...this.filterOptions,
          disciplines,
        };
      } else {
        this.filterOptions = {
          ...this.filterOptions,
          disciplines: [],
        };
      }
    },
    async searchServiceCost(pageIndex = 1) {
      const payload = {
        locationTypeId: this.filterModel.locationType?.id,
        serviceTypeId: this.filterModel.service?.id,
        disciplineTypeId: this.filterModel.discipline?.id,
        therapyDurationId: this.filterModel.duration?.id,
        serviceModalityTypeId: this.filterModel.modality?.id,
        rPMaxServiceCost: this.filterModel.cost,
        pageIndex,
        pageSize: this.pagination.pageSize,
      };
      const res = (await _searchServiceCost(payload)).data;

      this.results = res.data; //.map((x) => ({ ...x, editCost: null }));
      this.pagination = {
        ...this.pagination,
        count: res.count,
        currentPage: res.currentPage,
        pageCount: res.pageCount,
        totalItemCount: res.totalItemCount,
      };
    },
    async goToPageHandler(page) {
      await this.searchServiceCost(page);
    },
    onAddHandler() {
      this.isAddEditMode = true;
    },
    async onSearchHandler() {
      await this.searchServiceCost();
    },
    onCancelHandler() {
      this.isAddEditMode = false;
    },
    async onSaveCostHandler(model) {
      if (Object.values(model).length) {
        const payload = {
          serviceTypeCd: model.service?.id,
          disciplineTypeId: model.discipline?.id,
          serviceModalityTypeCd: model.modalityType?.id,
          therapyDurationId: model.duration?.id,
          locationTypeCd: model.locationType?.id,
          rPMaxServiceCost: +model.costMax,
          subDisciplineTypeId: model.subDisciplineTypeId?.id,
        };

        const { data: res } = await _saveCost(payload).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });

        if (res) {
          this.ShowToast(
            'Proceso Exitoso',
            'El servicio ha sido añadido exitosamente.',
            'success'
          );
          this.onCancelHandler();
          await this.searchServiceCost();
        }
      }
    },
    setEditMode(rowIndex, value) {
      this.results = this.results.map((x, idx) => ({
        ...x,
        isEditMode: rowIndex === idx ? value : null,
        editCost: rowIndex === idx ? x.rpMaxServiceCost : null,
      }));
    },
    async onSaveEditCostHandler({ id, editCost }) {
      if (id && editCost) {
        const payload = {
          rPMaxServiceCost: +editCost,
        };
        const { data: res } = await _editCost(id, payload).catch((err) => {
          this.ShowToast(
            'Alerta',
            err.response.data.message || 'error',
            'error'
          );
        });
        if (res) {
          this.ShowToast(
            'Proceso Exitoso',
            'El servicio ha sido actualizado exitosamente.',
            'success'
          );
          await this.searchServiceCost(this.pagination.currentPage);
        }
      }
    },
    async onDeleteCostHandler({ id }) {
      if (id) {
        const proceed = await this.ShowCancelModal(
          'Confirmación',
          '¿Usted está seguro que desea eliminar el sevicicio seleccionado?',
          'Sí',
          'No'
        );
        if (proceed) {
          const { data: res } = await _deleteCost(id).catch((err) => {
            this.ShowToast(
              'Alerta',
              err.response.data.message || 'error',
              'error'
            );
          });
          if (res) {
            this.ShowToast(
              'Proceso Exitoso',
              'El servicio ha sido eliminado exitosamente.',
              'success'
            );
            await this.searchServiceCost(this.pagination.currentPage);
          }
        }
      }
    },
  },
};
</script>
